import { type ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { type RouteComponent, type RouteElement, Router } from '@shared/router';
import { useHistoryRouter } from '@shared/router/hooks/history';
import { parser } from '@shared/router/parser';
import { useRouterState } from '@shared/router/state';
import DefaultFullPageLoader from '@shared/views/bootstrap/FullPageLoader';
import DefaultRouteError from '@shared/views/bootstrap/RouteError';
import DefaultRouteNotFound from '@shared/views/bootstrap/RouteNotFound';

interface BootstrapProps {
	routes: RouteElement[];

	renderErrorWith?: RouteComponent;
	renderNotFoundWith?: RouteComponent;
	renderLoadingWith?: () => ReactNode;

	children?: ReactNode;
}

export default function Bootstrap({
	routes,

	renderErrorWith: RouteError = DefaultRouteError,
	renderNotFoundWith: RouteNotFound = DefaultRouteNotFound,
	renderLoadingWith: FullPageLoader = DefaultFullPageLoader,

	children,
}: BootstrapProps): ReactNode {
	const { currentPath } = useRouterState();

	const path = currentPath ?? '';
	const [parsed] = parser(routes);

	useHistoryRouter();

	return (
		<ErrorBoundary resetKeys={[path]} fallback={<RouteError />}>
			<Suspense fallback={<FullPageLoader />}>
				<Router
					routes={parsed}
					currentPath={path}
					renderErrorWith={RouteError}
					renderNotFoundWith={RouteNotFound}
				/>
				{children}
			</Suspense>
		</ErrorBoundary>
	);
}
